import React from "react";
import moment from "moment";
import * as yup from "yup";
import { find } from "lodash";
import { message } from "antd";
import { Row, Column } from "../components/Dashboard/DashboardLayout.styles";
import { GLOBAL_FILTERS } from "../plannerConfig";
import {
  DATE_FORMAT,
  VALIDATION_MAP,
  BEGINNING_OF_TIME,
} from "../constants/grid.constants";
import { COPY_TO_CLIPBOARD } from "../constants/text.constants";
import routes from "../routes";
import { appliedFilterForDashboard } from "../components/Filters/applyFilter";

const DEFAULT_VIEW_LAYOUT_COMPONENT_MAPPING = {
  row: { component: Row, requiredProps: ["height"] },
  column: { component: Column, requiredProps: ["width"] },
};

// Collates the required props for each dashboard element.
const getRequiredProps = (item, requiredProps) =>
  requiredProps.reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: item[curr],
    }),
    {}
  );

// Recursively creates the dashboard structure. Iterates over the item collection,
// Creates the current level's structure and if there's any sub elements calls
// itself with those sub elements as the target collection.
export const mapViewLayoutToComponents = (
  items,
  componentMapping = DEFAULT_VIEW_LAYOUT_COMPONENT_MAPPING
) =>
  items.map((item, i) => {
    const {
      requiredProps,
      component: Element,
      dependencyData = () => {},
    } = componentMapping[item.type];
    return (
      <Element
        key={item.id || i}
        {...getRequiredProps(item, requiredProps)}
        {...dependencyData(item)}
      >
        {item.children &&
          mapViewLayoutToComponents(item.children, componentMapping)}
      </Element>
    );
  });

export const applyDayOffsetToWeek = (date, offset) =>
  moment(date)
    .add(Math.max(Number(offset) - 1, 0), "d")
    .format("YYYY-MM-DD");

const getStartOfWeekDate = (date) =>
  moment(date)
    .startOf("isoWeek")
    .format("YYYY-MM-DD");

const getEndOfWeekDate = (date) =>
  moment(date)
    .endOf("isoWeek")
    .format("YYYY-MM-DD");

export const getLiveWeekStartAndEnd = () => ({
  liveWeekStartDate: getStartOfWeekDate(),
  liveWeekEndDate: getEndOfWeekDate(),
});

export const generateRouteParams = (props) => {
  const {
    config,
    match: { params },
    location: { pathname },
    persistParams,
  } = props;

  moment.updateLocale("en", {
    week: {
      dow: config ? parseInt(config.dayOfWeekTimesliceStart, 0) || 1 : 1,
    },
  });

  if (!params.startDate && !persistParams.startDate) {
    params.startDate = "2018-11-05";
  }

  const startOfWeekDate = getStartOfWeekDate(
    params.startDate || (persistParams && persistParams.startOfWeekDate)
  );

  const endOfWeekDate = getEndOfWeekDate(
    params.startDate || (persistParams && persistParams.endOfWeekDate)
  );

  const startDate = applyDayOffsetToWeek(
    params.startDate || (persistParams && persistParams.startDate),
    params.offset
  );

  const endDate = applyDayOffsetToWeek(startDate, params.range);

  // Set the default values so the app doesn't break.
  // TODO: The backend should send a default param for this in config. And the
  // front end can just incorporate this value here as oppose a hardcoded value.
  if (!params.dataType && !persistParams.dataType) {
    params.dataType = "area";
  }

  if (!params.scenario && !persistParams.scenario) {
    params.scenario = 0;
  }

  if (!params.range && !persistParams.range) {
    params.range = 7;
  }

  if (!params.offset && !persistParams.offset) {
    params.offset = 0;
  }

  if (!params.limit && !persistParams.limit) {
    params.limit = 50;
  }

  if (!params.volumeType && !persistParams.volumeType) {
    params.volumeType = "volume";
  }

  return {
    ...params,
    startDate,
    endDate,
    startOfWeekDate,
    endOfWeekDate,
    pathname, // This is used to store the link for Redux-persist for rehydrating the default link.
  };
};

// This uses the raw path in route and replace it with params to generate a new link.
export const createNewPath = (path, params) => {
  let newPath = path;
  // :startDate in path is special - it uses startOfWeekDate param instead,
  // and have to hard set it.
  // TODO: Currently I cannot replace the :startDate with :startOfWeekDate, since
  // it will break the app. Future work is to find a solution so that I don't have
  // to hard wire things in here.
  newPath = newPath.replace(":startDate", params.startOfWeekDate);

  Object.keys(params).forEach((key) => {
    newPath = newPath.replace(`:${key}`, params[key]);
  });

  return newPath;
};

// Update the picker to change the links.
export const updateLink = (updateParamObj, props, replaceOnly) => {
  const { history, match, persistParams } = props;
  const { path } = match;
  const newParams = {
    ...persistParams,
    ...updateParamObj,
  };

  const newPath = createNewPath(path, newParams);

  if (replaceOnly) {
    history.replace(newPath);
  } else {
    history.push(newPath);
  }
};

export const getSetSequence = (
  props,
  getSequence,
  callback,
  additionalParams
) => {
  const { get, set } = props;
  const routeParams = { ...generateRouteParams(props), ...additionalParams };
  getSequence.forEach((arg) =>
    get(arg, {
      routeParams,
      callback,
    })
  );
  set("persistParams", { ...routeParams });
};

// "that" prop is usually "this" obj, so we can get the props and states and setStates.
//  This also dynamically generates the component states.
export const getFilters = (props, scenario, scenarioCompare, callback) => {
  const { get, match } = props;
  const getMatchScenario = match ? match.params.scenario : scenario;
  get([GLOBAL_FILTERS], {
    routeParams: {
      scenario: scenario || getMatchScenario,
      scenarioCompare: scenarioCompare || getMatchScenario,
    },
    callback,
  });
};

export const filterScenarios = (scenarios, persistParams) =>
  scenarios.filter(
    (item) => item.fromDate === persistParams.startOfWeekDate || item.id === 0
  );

export const validateRow = (data, columnMeta) => {
  const schemaShape = {};
  columnMeta.forEach(({ type, name, required }) => {
    let useValidate = VALIDATION_MAP[type];
    if (!required && useValidate) useValidate = useValidate.nullable();
    if (useValidate) schemaShape[name] = useValidate;
  });
  const schema = yup.object().shape(schemaShape);

  return schema.validate(data);
};

// Gets the last item in a list.
export const last = (list) => list.slice(-1)[0];

// Gets everything but the last item in a list.
export const allButLast = (list) => list.slice(0, -1);

// 'camelCase' -> 'camel_case'.
export const camelToSnake = (str) =>
  str && typeof str === "string"
    ? str.replace(/([a-z][A-Z])/g, (g) => `${g[0]}_${g[1].toLowerCase()}`)
    : str;

// 'camel_case' -> 'camelCase'
export const snakeToCamel = (str) =>
  str && typeof str === "string"
    ? str.replace(/_([a-z])/g, (g) => g[1].toUpperCase())
    : str;

// Gives a string representation of an Object.
export const objectToString = (obj) =>
  Object.keys(obj)
    .map((key) => `${key}: ${obj[key]}`)
    .join(", ");

// Returns whether or not two sets intersect.
export const intersection = (s1, s2) => s1.some((x) => s2.includes(x));

export const plural = (noun, count) => `${noun}${count === 1 ? "" : "s"}`;

// Turns a dictionary of priority-ordered predicates into a list of string representations of those predicates.
export const predicatesToText = (predicates, textMap) =>
  Object.keys(predicates)
    .filter((key) => predicates[key])
    .map((key) => textMap[key])[0];

// Filter an object by a predicate on its keys.
export const filterObjectByKey = (object, predicate) =>
  Object.keys(object).reduce(
    (a, key) => ({ ...a, ...(predicate(key) ? { [key]: object[key] } : {}) }),
    {}
  );

// Find y percent of x.
export const percentage = (x, y) => (y / 100) * x;

// This function constructs the sorting/ordering query string for Bkt-grid.
export const orderingString = (column, meta) => {
  if (!column) {
    return;
  }
  const metaColumns = meta.currentMeta.columns;
  const names = column.split(",");
  const replacedNames = names.map((name) => {
    const isLookUp = find(metaColumns, {
      name: name.replace("-", ""),
      type: "lookup",
    });
    return isLookUp ? `${name}__name` : name;
  });
  return replacedNames.join(",");
};

// This function constructs the filter query string for Bkt-grid.
export const buildFilters = (bFilters) =>
  Object.keys(bFilters)
    .map((key) => bFilters[key].setServerFilter(key, bFilters[key]))
    .reduce((acc, sf) => {
      const filter = sf ? Object.keys(sf)[0] : [];
      return { ...acc, [filter]: sf && filter ? sf[filter].value : "" };
    }, {});

export const generateTSVForCopyToClipboard = (json) => {
  const replacer = (key, value) => (value === null ? " " : value); // specify how you want to handle null values here
  const heading = Object.keys(json[0]);
  let tsv = json.map((row) =>
    heading
      .map((fieldName) => JSON.stringify(row[fieldName], replacer))
      .join("\t")
  );
  tsv.unshift(heading.join("\t"));
  tsv = tsv.join("\r\n");
  return tsv;
};

export const copyToClipboard = (str) => {
  // Create temporary textarea element
  const tempTextArea = document.createElement("textarea");
  tempTextArea.value = str;
  tempTextArea.setAttribute("readonly", "");
  tempTextArea.style.position = "absolute";
  tempTextArea.style.top = "-9999px";
  document.body.appendChild(tempTextArea);
  // Select text inside the temporary textarea.
  tempTextArea.select();
  // Copy text to clipboard
  document.execCommand("copy");
  // Remove temporary element
  document.body.removeChild(tempTextArea);
};

export const copyJSONToClipboard = (json) => {
  copyToClipboard(generateTSVForCopyToClipboard(json));
  message.info(COPY_TO_CLIPBOARD);
};

export const handleClickToViewScenario = (data, isDashboard, props) => {
  const { history, persistParams } = props;
  const { id, fromDate } = data;

  const startDate =
    fromDate !== BEGINNING_OF_TIME ? fromDate : persistParams.startDate;

  const findRoute = routes.find(
    (route) => route.name === (isDashboard ? "Dashboard" : "Data")
  );
  const { path } = findRoute;

  updateLink(
    {
      scenario: id,
      startDate,
      startOfWeekDate: startDate,
      range: 7,
      offset: 0,
    },
    {
      match: { path },
      persistParams,
      history,
    }
  );
};

export const getLocaleNumber = (num) => {
  try {
    return num.toLocaleString();
  } catch (e) {
    return num;
  }
};

export const getLargestValuesForCols = (set) => {
  const targetSet = set || [];
  return (
    targetSet.reduce((a, c) => {
      const current = { ...a };
      for (const key in c) {
        if (!current[key] || current[key] < c[key]) {
          current[key] = c[key];
        }
      }
      return current;
    }, {}) || {}
  );
};

export const asFormattedValue = (num) => {
  try {
    return num.toLocaleString();
  } catch (e) {
    return num;
  }
};
